import React, { useState, useCallback, useMemo } from 'react'
import { useMutation } from 'react-query'

import { useClient } from '@hooks/useClient'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import {
  StoryIdsModalProps,
  WithStoryIdsModalProps,
  StoryIdTag,
  SubmitResult,
} from './interface'

// Helper: Create hash from array of IDs to check for changes
const createIdsHash = (ids: number[]): string => {
  return [...ids].sort((a, b) => a - b).join(',')
}

// Helper: parse CSV file - simple column of IDs, no header
const parseCSV = async (file: File): Promise<number[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = e => {
      try {
        const text = e.target?.result as string
        const lines = text.split(/\r?\n/)
        const ids: number[] = []

        // Parse each line as an ID (single column, no header)
        lines.forEach(line => {
          const trimmed = line.trim()
          if (!trimmed) return

          // Handle comma-separated values in a line too
          const values = trimmed.split(/[,;\t]/)
          values.forEach(value => {
            const num = parseInt(value.trim(), 10)
            if (!Number.isNaN(num) && num > 0) {
              ids.push(num)
            }
          })
        })

        resolve(ids)
      } catch {
        reject(new Error('Failed to parse CSV file'))
      }
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsText(file)
  })
}

export function withStoryIdsModal(Component: React.FC<WithStoryIdsModalProps>) {
  function WithStoryIdsModal(props: StoryIdsModalProps) {
    const { bannerId, mode, existingIds = [], onSuccess } = props
    const client = useClient()
    const modal = useModal({ modal: 'storyIdsModal' })

    // State
    const [storyIds, setStoryIds] = useState<StoryIdTag[]>([])
    const [isAutoSort, setIsAutoSort] = useState(false)
    const [isValidated, setIsValidated] = useState(false)
    const [lastValidatedHash, setLastValidatedHash] = useState('')
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [submitResult, setSubmitResult] = useState<SubmitResult | null>(null)

    // Get current IDs hash
    const currentIdsHash = useMemo(
      () => createIdsHash(storyIds.map(t => t.id)),
      [storyIds]
    )

    // Check for duplicate IDs
    const hasDuplicateIds = useMemo(
      () => storyIds.some(t => t.isDuplicate),
      [storyIds]
    )

    // Check if can submit
    const canSubmit = useMemo(() => {
      if (!isValidated) return false
      if (currentIdsHash !== lastValidatedHash) return false
      if (storyIds.length === 0) return false
      if (storyIds.some(t => t.isValid === false)) return false
      if (storyIds.some(t => t.isDuplicate)) return false
      return true
    }, [isValidated, currentIdsHash, lastValidatedHash, storyIds])

    // Validate mutation - use checkBookExist for validation
    // checkBookExist returns true if ALL IDs exist, or throws error with invalid IDs in extras.writerIds
    const BATCH_SIZE = 100 // Number of IDs per batch
    const validateMutation = useMutation(
      async ({
        idsToValidate,
        currentTags,
      }: {
        idsToValidate: number[]
        currentTags: typeof storyIds
      }) => {
        // Filter only IDs that haven't been validated yet (isValid === null) AND are not duplicates
        const uncheckedIds = idsToValidate.filter(id => {
          const tag = currentTags.find(t => t.id === id)
          return tag?.isValid === null && !tag?.isDuplicate
        })

        // If no new IDs to validate, return existing results
        if (uncheckedIds.length === 0) {
          return {
            validIds: currentTags
              .filter(t => t.isValid === true)
              .map(t => t.id),
            invalidIds: currentTags
              .filter(t => t.isValid === false)
              .map(t => t.id),
            uncheckedIds: [],
          }
        }

        const newValidIds: number[] = []
        const newInvalidIds: number[] = []

        // Split IDs into batches
        const batches: number[][] = []
        for (let i = 0; i < uncheckedIds.length; i += BATCH_SIZE) {
          batches.push(uncheckedIds.slice(i, i + BATCH_SIZE))
        }

        // Process each batch using Promise.all for parallel execution where possible
        const batchResults = await Promise.all(
          batches.map(async batch => {
            try {
              // Check entire batch at once
              const allExist = await client?.promotionClient.checkBookExist(
                batch
              )

              if (allExist === true) {
                // All IDs in this batch are valid
                return { validIds: batch, invalidIds: [] as number[] }
              }
              return { validIds: [] as number[], invalidIds: [] as number[] }
            } catch (error: any) {
              // Extract invalid IDs from error response extras.writerIds
              const invalidIdsFromError = error?.response?.errors?.[0]
                ?.extensions?.extras?.writerIds as number[] | undefined

              if (invalidIdsFromError && invalidIdsFromError.length > 0) {
                // Add invalid IDs from error
                const invalidSet = new Set(invalidIdsFromError)
                const validInBatch = batch.filter(id => !invalidSet.has(id))
                return {
                  validIds: validInBatch,
                  invalidIds: invalidIdsFromError,
                }
              }
              // Unknown error - mark all as invalid
              return { validIds: [] as number[], invalidIds: batch }
            }
          })
        )

        // Aggregate results
        batchResults.forEach(result => {
          newValidIds.push(...result.validIds)
          newInvalidIds.push(...result.invalidIds)
        })

        // Combine results from previous validation + new validation
        const previousValidIds = currentTags
          .filter(t => t.isValid === true)
          .map(t => t.id)
        const allValidIds = [...previousValidIds, ...newValidIds]

        return {
          validIds: allValidIds,
          invalidIds: [
            ...currentTags.filter(t => t.isValid === false).map(t => t.id),
            ...newInvalidIds,
          ],
          uncheckedIds,
        }
      },
      {
        onSuccess: result => {
          // Update tags with validation result
          setStoryIds(prev => {
            return prev.map(tag => {
              // If ID is in uncheckedIds (just validated), update isValid
              if (result.uncheckedIds.includes(tag.id)) {
                return { ...tag, isValid: result.validIds.includes(tag.id) }
              }
              // If not in uncheckedIds, keep existing status
              return tag
            })
          })
          setIsValidated(true)
          setLastValidatedHash(currentIdsHash)
        },
      }
    )

    // Submit mutation - batch to avoid DB connection pool exhaustion
    const SUBMIT_BATCH_SIZE = 50 // Limit per API call to prevent connection issues
    const submitMutation = useMutation(
      async (ids: number[]) => {
        try {
          const allSuccessIds: number[] = []
          const allFailedIds: number[] = []

          // Split IDs into batches
          const batches: number[][] = []
          for (let i = 0; i < ids.length; i += SUBMIT_BATCH_SIZE) {
            batches.push(ids.slice(i, i + SUBMIT_BATCH_SIZE))
          }

          // Submit each batch sequentially with delay
          for (let i = 0; i < batches.length; i += 1) {
            const batch = batches[i]
            try {
              // eslint-disable-next-line no-await-in-loop
              await client?.highlightBannerClient.createGroupBookBanner(
                bannerId,
                batch
              )
              allSuccessIds.push(...batch)

              // Add small delay between batches to let DB connections release
              if (i < batches.length - 1) {
                // eslint-disable-next-line no-await-in-loop
                await new Promise(resolve => {
                  setTimeout(resolve, 500)
                })
              }
            } catch (error: any) {
              // This batch failed
              allFailedIds.push(...batch)
            }
          }

          if (allFailedIds.length === 0) {
            return {
              success: true,
              successIds: allSuccessIds,
              failedIds: [],
              message: `เพิ่ม Story IDs สำเร็จ ${allSuccessIds.length} รายการ`,
            } as SubmitResult
          }
          if (allSuccessIds.length > 0) {
            return {
              success: false,
              successIds: allSuccessIds,
              failedIds: allFailedIds,
              message: `เพิ่มสำเร็จ ${allSuccessIds.length} รายการ, ล้มเหลว ${allFailedIds.length} รายการ`,
            } as SubmitResult
          }
          return {
            success: false,
            successIds: [],
            failedIds: allFailedIds,
            message: 'เกิดข้อผิดพลาดในการบันทึกทั้งหมด',
          } as SubmitResult
        } catch (error: any) {
          return {
            success: false,
            successIds: [],
            failedIds: ids,
            message: error?.message || 'เกิดข้อผิดพลาดในการบันทึก',
          } as SubmitResult
        }
      },
      {
        onSuccess: result => {
          setIsSubmitted(true)
          setSubmitResult(result)
          if (result.success && onSuccess) {
            onSuccess()
          }
        },
      }
    )

    // Create set of existing banner IDs for duplicate checking
    const existingIdsSet = useMemo(() => new Set(existingIds), [existingIds])

    // Actions
    const addStoryIds = useCallback(
      (newIds: number[]) => {
        setStoryIds(prev => {
          // Filter duplicates within newIds itself first
          const uniqueNewIds = [...new Set(newIds)]
          // Then filter against existing IDs in modal
          const existingSet = new Set(prev.map(t => t.id))
          const filteredIds = uniqueNewIds.filter(id => !existingSet.has(id))
          const newTags = filteredIds.map(id => ({
            id,
            isValid: null as boolean | null,
            isDuplicate: existingIdsSet.has(id), // Check if already in banner
          }))
          return [...prev, ...newTags]
        })
        // Reset validation when IDs change
        setIsValidated(false)
      },
      [existingIdsSet]
    )

    const removeStoryId = useCallback((id: number) => {
      setStoryIds(prev => {
        const tagToRemove = prev.find(t => t.id === id)
        const remaining = prev.filter(t => t.id !== id)

        // If removed tag is invalid or pending (null), don't reset validation
        // because remaining IDs are still validated
        if (tagToRemove?.isValid === false || tagToRemove?.isValid === null) {
          setLastValidatedHash(createIdsHash(remaining.map(t => t.id)))
        } else {
          // If removed tag is valid, reset validation
          setIsValidated(false)
        }

        return remaining
      })
    }, [])

    const updateStoryId = useCallback(
      (oldId: number, newId: number) => {
        setStoryIds(prev => {
          // If newId already exists in modal, remove the old tag
          const isDuplicateInModal = prev.some(
            t => t.id === newId && t.id !== oldId
          )
          if (isDuplicateInModal) {
            return prev.filter(t => t.id !== oldId)
          }
          // If not duplicate in modal, update with isDuplicate check against banner
          return prev.map(t =>
            t.id === oldId
              ? {
                  id: newId,
                  isValid: null,
                  isDuplicate: existingIdsSet.has(newId),
                }
              : t
          )
        })
        setIsValidated(false)
      },
      [existingIdsSet]
    )

    const removeInvalidIds = useCallback(() => {
      setStoryIds(prev => {
        const validOnly = prev.filter(t => t.isValid !== false)
        // Update hash to match remaining valid IDs
        setLastValidatedHash(createIdsHash(validOnly.map(t => t.id)))
        return validOnly
      })
      // Don't reset isValidated because remaining IDs are still validated
    }, [])

    const removeDuplicateIds = useCallback(() => {
      setStoryIds(prev => {
        const nonDuplicates = prev.filter(t => !t.isDuplicate)
        // Update hash to match remaining IDs
        setLastValidatedHash(createIdsHash(nonDuplicates.map(t => t.id)))
        return nonDuplicates
      })
      // Don't reset isValidated because remaining IDs are still validated
    }, [])

    const clearAllIds = useCallback(() => {
      setStoryIds([])
      setIsValidated(false)
    }, [])

    const toggleAutoSort = useCallback(() => {
      setIsAutoSort(prev => !prev)
    }, [])

    const handleValidate = useCallback(
      async (pendingInputIds?: number[]) => {
        // If there are pendingInputIds, add them first
        let currentTags = storyIds
        if (pendingInputIds && pendingInputIds.length > 0) {
          // Create new tags from pending input
          const currentExistingIds = storyIds.map(t => t.id)
          const newIds = pendingInputIds.filter(
            id => !currentExistingIds.includes(id)
          )
          const newTags = newIds.map(id => ({
            id,
            isValid: null as boolean | null,
            isDuplicate: existingIdsSet.has(id), // Check if already in banner
          }))
          currentTags = [...storyIds, ...newTags]
          // Update state
          setStoryIds(currentTags)
          setIsValidated(false)
        }

        const ids = currentTags.map(t => t.id)
        if (ids.length === 0) return

        // Validate and then update the hash to match validated IDs
        await validateMutation.mutateAsync({ idsToValidate: ids, currentTags })

        // After successful validation, update lastValidatedHash to match current IDs
        // This is needed because currentIdsHash in mutation.onSuccess is stale when pendingInputIds were added
        setLastValidatedHash(createIdsHash(ids))
      },
      [storyIds, validateMutation, existingIdsSet]
    )

    const handleSubmit = useCallback(async () => {
      const validIds = storyIds.filter(t => t.isValid).map(t => t.id)
      if (validIds.length === 0) return
      await submitMutation.mutateAsync(validIds)
    }, [storyIds, submitMutation])

    const handleClose = useCallback(() => {
      modal.hide()
    }, [modal])

    const parseCSVFile = useCallback(async (file: File): Promise<number[]> => {
      return parseCSV(file)
    }, [])

    // Get displayed story IDs (sorted if auto-sort is enabled)
    const displayedStoryIds = useMemo(() => {
      if (!isAutoSort) return storyIds
      return [...storyIds].sort((a, b) => a.id - b.id)
    }, [storyIds, isAutoSort])

    const newProps: WithStoryIdsModalProps = {
      ...props,
      storyIds: displayedStoryIds,
      isAutoSort,
      isValidating: validateMutation.isLoading,
      isSubmitting: submitMutation.isLoading,
      isValidated,
      canSubmit,
      isSubmitted,
      submitResult,
      lastValidatedHash,
      hasDuplicateIds,
      addStoryIds,
      removeStoryId,
      updateStoryId,
      removeInvalidIds,
      removeDuplicateIds,
      clearAllIds,
      toggleAutoSort,
      handleValidate,
      handleSubmit,
      handleClose,
      parseCSVFile,
    }

    return <Component {...newProps} />
  }

  return WithStoryIdsModal
}
